/**
 * One rasterised texture per scene and sub-resource, or per content for a
 * texture built off the main thread, shared, bounded and disposed. Many nodes share one gradient, and each raster costs
 * `width x height x 4` bytes and an upload. The cache owns the lifetime through
 * an `LRUCache`: consumers borrow and never dispose.
 */
import { identityKey } from '../../utils/identityKey.js';
import { LRUCache } from '../LRUCache.js';
/**
 * Distinct procedural textures kept resident: generous for real scenes, which
 * declare a handful, and a bound for a pathological one. An eviction frees its buffer.
 */
const MAX_ENTRIES = 64;
const cache = new LRUCache(MAX_ENTRIES, (_key, texture) => texture.dispose());
/**
 * The shared texture for `subResourceId` in this scene, rasterised on first
 * request. Null, uncached, when `rasterize` declines. The texture is borrowed:
 * the cache disposes it, callers never do.
 */
export function proceduralTexture(internalResources, subResourceId, rasterize) {
    const key = proceduralTextureKey(internalResources, subResourceId);
    const cached = cache.get(key);
    if (cached)
        return cached;
    const rasterized = rasterize();
    if (!rasterized)
        return null;
    cache.set(key, rasterized);
    return rasterized;
}
/**
 * The cache key a consumer must pin while it holds the texture. Without a pin,
 * the 65th gradient disposes the least-recently-used one while a mounted slot
 * still samples it, and nothing re-rasterises: its memo deps have not changed.
 */
export function proceduralTextureKey(internalResources, subResourceId) {
    return `${identityKey(internalResources)}:${subResourceId}`;
}
/** The texture cached under `key`, for a build keyed on its content rather than its scene. */
export function cachedProceduralTexture(key) {
    return cache.get(key);
}
/** Caches a texture built under a content key. The cache owns it from now on. */
export function cacheProceduralTexture(key, texture) {
    cache.set(key, texture);
}
/** Frees each cached texture from every renderer, and keeps it cached: a renderer uploads it again. */
export function releaseProceduralTextures() {
    for (const texture of cache.values())
        texture.dispose();
}
/** Hold `key` resident for as long as a consumer is mounted. */
export function pinProceduralTexture(key) {
    cache.pin(key);
}
/** Release a pin taken by `pinProceduralTexture`. */
export function unpinProceduralTexture(key) {
    cache.unpin(key);
}
/** Drops every entry, disposing each now or, while pinned, at its last unpin. */
export function clearProceduralTextures() {
    cache.clear();
}
//# sourceMappingURL=proceduralTextureCache.js.map